// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import styles from '@app/features/channel/components/ChannelSourcePreview.module.css';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import {getGroupDMAccentColor} from '@app/features/channel/utils/GroupDMColorUtils';
import Guilds from '@app/features/guild/state/Guilds';
import {getGuildIconDisplayInitials, getInitialsLength} from '@app/features/guild/utils/GuildInitialsUtils';
import {Link} from '@app/features/platform/components/router/React';
import {MentionBadge} from '@app/features/ui/components/MentionBadge';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import Users from '@app/features/user/state/Users';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import * as StringUtils from '@app/lib/strings';
import {MEDIA_PROXY_ICON_SIZE_DEFAULT} from '@app/vendor/constants/MediaProxyAssetSizes';
import type {MediaProxyImageSize} from '@app/vendor/constants/MediaProxyImageSizes';
import {plural} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CaretRightIcon, HashIcon, NotePencilIcon, UserIcon, UsersIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

type SubtextTone = 'muted' | 'primary';
interface ChannelSourcePreviewProps {
	channel: Channel;
	onClick?: () => void;
	linkTo?: string;
	onContextMenu?: (event: React.MouseEvent<HTMLElement>) => void;
	mentionCount?: number;
	avatarSize?: MediaProxyImageSize;
	variant?: 'default' | 'inline';
	showChannelIcon?: boolean;
	className?: string;
}
function getChannelDisplayName(channel: Channel): string {
	if (channel.isPrivate()) {
		return ChannelUtils.getDMDisplayName(channel);
	}
	return channel.name?.trim() || ChannelUtils.getName(channel);
}
function getGroupDmMemberCount(channel: Channel): number {
	const currentUserId = Authentication.currentUserId;
	if (!currentUserId) return channel.recipientIds.length;
	const memberIds = new Set(channel.recipientIds);
	memberIds.add(currentUserId);
	return memberIds.size;
}
function renderAvatarImage(props: {url: string; label: string; size: number}): React.ReactNode {
	const {url, label, size} = props;
	return (
		<div
			className={styles.avatar}
			style={{width: size, height: size, backgroundImage: `url(${url})`}}
			role="img"
			aria-label={label}
		/>
	);
}
function renderAvatarFallback(props: {
	label: string;
	size: number;
	background?: string;
	content: React.ReactNode;
	initialsLength?: string;
}): React.ReactNode {
	const {label, size, background, content, initialsLength} = props;
	return (
		<div
			className={styles.avatar}
			style={{width: size, height: size, backgroundColor: background}}
			role="img"
			aria-label={label}
			data-initials-length={initialsLength}
		>
			{content}
		</div>
	);
}
function renderChannelAvatar(channel: Channel, size: MediaProxyImageSize): React.ReactNode {
	if (channel.guildId) {
		const guild = Guilds.getGuild(channel.guildId);
		if (!guild) {
			return renderAvatarFallback({
				label: 'Channel',
				size,
				content: <HashIcon className={styles.fallbackIcon} weight="bold" />,
			});
		}
		const iconUrl = AvatarUtils.getGuildIconURL({id: guild.id, icon: guild.icon}, true);
		if (iconUrl) {
			return renderAvatarImage({url: iconUrl, label: guild.name, size});
		}
		const rawInitials = StringUtils.getInitialsFromName(guild.name);
		const initials = getGuildIconDisplayInitials(rawInitials);
		const initialsLength = getInitialsLength(rawInitials);
		return renderAvatarFallback({
			label: guild.name,
			size,
			content: <span className={styles.initials}>{initials}</span>,
			initialsLength,
		});
	}
	if (channel.isDM()) {
		const recipientId = channel.getRecipientId();
		const recipient = recipientId ? Users.getUser(recipientId) : null;
		if (recipient) {
			const url = AvatarUtils.getUserAvatarURL({id: recipient.id, avatar: recipient.avatar}, false);
			return renderAvatarImage({url, label: recipient.tag, size});
		}
		return renderAvatarFallback({
			label: 'Direct message',
			size,
			content: <UserIcon className={styles.fallbackIcon} weight="bold" />,
		});
	}
	if (channel.isGroupDM()) {
		const iconUrl = AvatarUtils.getChannelIconURL(
			{id: channel.id, icon: channel.icon},
			MEDIA_PROXY_ICON_SIZE_DEFAULT,
			true,
		);
		if (iconUrl) {
			return renderAvatarImage({url: iconUrl, label: 'Group', size});
		}
		const accentColor = getGroupDMAccentColor(channel.id);
		return renderAvatarFallback({
			label: 'Group',
			size,
			background: accentColor,
			content: <UsersIcon className={styles.fallbackIcon} weight="bold" />,
		});
	}
	if (channel.isPersonalNotes()) {
		return renderAvatarFallback({
			label: 'Personal notes',
			size,
			content: <NotePencilIcon className={styles.fallbackIcon} weight="bold" />,
		});
	}
	return renderAvatarFallback({
		label: 'Channel',
		size,
		content: <HashIcon className={styles.fallbackIcon} weight="bold" />,
	});
}
export const ChannelSourcePreview = observer(function ChannelSourcePreview({
	channel,
	onClick,
	linkTo,
	onContextMenu,
	mentionCount,
	avatarSize,
	variant = 'default',
	showChannelIcon = true,
	className,
}: ChannelSourcePreviewProps) {
	const {t} = useLingui();
	const guild = channel.guildId ? (Guilds.getGuild(channel.guildId) ?? null) : null;
	const category = channel.parentId ? (Channels.getChannel(channel.parentId) ?? null) : null;
	const isGroupDm = channel.isGroupDM();
	const isUserDm = channel.isDM();
	const channelDisplayName = getChannelDisplayName(channel);
	const displayName = isUserDm ? `@${channelDisplayName}` : channelDisplayName;
	const resolvedAvatarSize: MediaProxyImageSize = avatarSize ?? (variant === 'inline' ? 24 : 32);
	const {subtext, subtextTone} = useMemo(() => {
		if (isGroupDm) {
			const memberCount = getGroupDmMemberCount(channel);
			const label = plural(
				{count: memberCount},
				{
					one: '# Member',
					other: '# Members',
				},
			);
			return {subtext: label, subtextTone: 'primary' as SubtextTone};
		}
		if (guild) {
			if (category?.name) {
				return {
					subtext: (
						<span className={styles.subtextBreadcrumb}>
							<span>{guild.name}</span>
							<CaretRightIcon className={styles.subtextChevron} weight="bold" />
							<span>{category.name}</span>
						</span>
					),
					subtextTone: 'muted' as SubtextTone,
				};
			}
			return {subtext: guild.name, subtextTone: 'muted' as SubtextTone};
		}
		return {subtext: null, subtextTone: 'muted' as SubtextTone};
	}, [category?.name, channel, guild, isGroupDm, t]);
	const nameContent = <span className={styles.nameText}>{displayName}</span>;
	const nameAriaLabel = t`Jump to ${displayName}`;
	const renderNameAction = () => {
		if (linkTo) {
			return (
				<FocusRing offset={-2} ringClassName={styles.focusRingTight}>
					<Link to={linkTo} className={styles.nameLink} aria-label={nameAriaLabel}>
						{nameContent}
					</Link>
				</FocusRing>
			);
		}
		if (onClick) {
			return (
				<FocusRing offset={-2} ringClassName={styles.focusRingTight}>
					<button type="button" className={styles.nameButton} onClick={onClick} aria-label={nameAriaLabel}>
						{nameContent}
					</button>
				</FocusRing>
			);
		}
		return nameContent;
	};
	const containerInteractionProps = onContextMenu ? {onContextMenu} : {};
	return (
		<div
			className={clsx(styles.container, variant === 'inline' && styles.inline, className)}
			{...containerInteractionProps}
		>
			{renderChannelAvatar(channel, resolvedAvatarSize)}
			<div className={styles.textContainer}>
				<div className={styles.nameRow}>
					{showChannelIcon && channel.guildId
						? ChannelUtils.getIcon(channel, {className: styles.channelIcon, weight: 'bold'})
						: null}
					{renderNameAction()}
					{mentionCount != null && mentionCount > 0 ? <MentionBadge mentionCount={mentionCount} size="small" /> : null}
				</div>
				{subtext ? (
					<div
						className={clsx(styles.subtext, subtextTone === 'primary' ? styles.subtextPrimary : styles.subtextMuted)}
					>
						{subtext}
					</div>
				) : null}
			</div>
		</div>
	);
});
